import { useEffect, useMemo, useState } from 'react'
import { WellStratumFieldsEdit } from '../../../../types/form-types'
import { useLookup } from '../../../hooks/use-api'
import { LookupData } from '../../../../types/api-types'
import LookupField from '../../wells/lookup-field'
import {
  extractAllowableFields,
  stratumHasExistingFractionType,
  resetFractionTypeDescriptorFields,
} from './fraction-type-utils'
import {
  fractionDescriptors,
  FractionTypeAllowableFields,
  FractionTypeCardEditProps,
  FractionTypeDescriptors,
  FractionTypeKeys,
} from './lithology-types'
import { CollapsibleCard, FormField, FormFieldRow } from '@msaf/core-react'

export default function FractionCardEdit({
  fractionType,
  stratum,
  isLoading,
  setValue,
  validationErrors,
  isExistingOrDecommissioned,
}: FractionTypeCardEditProps) {
  const [fractionTypeLookupId, setFractionTypeLookupId] = useState<number | undefined>()

  const { data } = useLookup('lithologyFraction')
  const [allowableFields, setAllowableFields] = useState<string[]>()
  const [fractionDetailsValues, setFractionDetailsValue] = useState<Partial<WellStratumFieldsEdit>>()
  const [fractionTypeData, setFractionTypeData] = useState<Array<LookupData>>()
  const [showNewEditCard, setShowNewEditCard] = useState<boolean>(fractionType === 'major')

  useEffect(() => {
    if (data?.length > 0) {
      setFractionTypeData(data)
    }
  }, [data])

  useEffect(() => {
    const fractionTypeLookupId =
      `${fractionType}Fraction` in stratum ? Number(stratum[`${fractionType}Fraction` as keyof typeof stratum]) : 0
    setFractionTypeLookupId(fractionTypeLookupId)
  }, [stratum, fractionType])

  useEffect(() => {
    setFractionDetailsValue(stratum)
    setShowNewEditCard(!!validationErrors[`${fractionType}Fraction`] || fractionType === 'major')
  }, [stratum, validationErrors, fractionType])

  // Set allowable fields for edit card based when the lithology fraction type changes or
  // when stratum changes.
  useEffect(() => {
    // Prevent effect running if these have not yet been defined
    if (!fractionTypeData || !fractionTypeLookupId) {
      return
    }
    const fieldName = fractionTypeData?.find((item: any) => item.id === fractionTypeLookupId)
    const allowableFields = extractAllowableFields(fieldName?.extendedAttributes1)
    if (allowableFields !== null) {
      setAllowableFields(allowableFields)
    }
  }, [fractionTypeLookupId, fractionTypeData, data, stratum])

  // Make sure the other fields are reset for both display values and in edit form state when the fraction type changes.
  const handleFractionTypeChange = (value: string | undefined) => {
    setFractionTypeLookupId(Number(value))

    if (value === undefined) {
      setValue(`${fractionType}Fraction`, '')
    } else {
      setValue(`${fractionType}Fraction`, value)
    }
    // When the lookup field changes reset all fields / descriptors (colour, rounding etc)associated with that fraction type (major/minor etc)
    resetFractionTypeDescriptorFields(fractionType, '', stratum, setValue)
    const displayValues: { [key: string]: string | null } = {}
    // Reset individual display values back to '' to ensure no Existing settings displayed
    for (const descriptor of fractionDescriptors) {
      if (`${fractionType}${descriptor}` in stratum) {
        const key = `${fractionType}${descriptor}`
        displayValues[key] = null
      }
      setFractionDetailsValue(displayValues)
    }
  }

  // Reset the display value for the field to match currently selected value
  // setValue for use with the useEditForm hook
  const handleFractionDescriptorValueChange = (key: keyof WellStratumFieldsEdit, value: string) => {
    const newValue: { [key: string]: string } = {}
    newValue[key] = value
    setFractionDetailsValue({ ...fractionDetailsValues, ...newValue })
    setValue(key, value)
  }

  const getLookupFieldValue = (descriptor: FractionTypeDescriptors) => {
    if (fractionDetailsValues && `${fractionType}${descriptor}` in fractionDetailsValues) {
      return `${fractionDetailsValues[`${fractionType}${descriptor}` as keyof typeof fractionDetailsValues]}`
    }

    return ''
  }

  const removeFractionEditCard = () => {
    handleFractionTypeChange('')
    setShowNewEditCard(false)
  }

  const shouldDisplayCard = stratumHasExistingFractionType(fractionType, stratum) || showNewEditCard

  const showFracturingField = useMemo(() => {
    return fractionType === FractionTypeKeys.MAJOR || fractionType === FractionTypeKeys.SECONDARY
  }, [fractionType])

  return (
    <CollapsibleCard
      heading={`${fractionType.charAt(0).toUpperCase()}${fractionType.slice(1)} Fraction`}
      key={fractionType}
      shouldDisplayCard={shouldDisplayCard}
      onCollapse={removeFractionEditCard}
      onOpen={() => setShowNewEditCard(true)}
    >
      <>
        <FormFieldRow>
          <FormField
            validationMessages={validationErrors[`${fractionType}Fraction` as keyof typeof stratum]}
            isSkeleton={isLoading}
            labelId={`${fractionType}FractionType`}
            label='Type'
            isRequired={`${fractionType}Fraction` === 'majorFraction' && isExistingOrDecommissioned}
          >
            <LookupField
              fieldId='lithologyFraction'
              isSkeleton={isLoading}
              value={`${stratum[`${fractionType}Fraction` as keyof typeof stratum] ?? ''}`}
              labelledBy={`${fractionType}FractionType`}
              setValue={({ value }) => handleFractionTypeChange(value)}
              isInvalid={!!validationErrors[`${fractionType}Fraction` as keyof typeof stratum]}
            />
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField
            validationMessages={validationErrors[`${fractionType}FractionGrainsize` as keyof typeof stratum]}
            isSkeleton={isLoading}
            labelId={`${fractionType}FractionGrainsize`}
            label='Grain size'
          >
            <LookupField
              fieldId='lithologyGrainsize'
              isSkeleton={isLoading}
              isDisabled={!allowableFields?.includes(FractionTypeAllowableFields.GS)}
              value={getLookupFieldValue(FractionTypeDescriptors.GRAINSIZE)}
              labelledBy={`${fractionType}FractionGrainsize`}
              setValue={({ value }) => handleFractionDescriptorValueChange(`${fractionType}Grainsize`, value)}
            />
          </FormField>

          <FormField
            validationMessages={validationErrors[`${fractionType}RockStrength` as keyof typeof stratum]}
            isSkeleton={isLoading}
            labelId={`${fractionType}FractionRockStrength`}
            label='Rock strength'
          >
            <LookupField
              fieldId='lithologyRockStrength'
              isSkeleton={isLoading}
              isDisabled={!allowableFields?.includes(FractionTypeAllowableFields.ST)}
              value={getLookupFieldValue(FractionTypeDescriptors.ROCKSTRENGTH)}
              labelledBy={`${fractionType}FractionRockStrength`}
              setValue={({ value }) => handleFractionDescriptorValueChange(`${fractionType}RockStrength`, value)}
            />
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField
            validationMessages={validationErrors[`${fractionType}FractionSorting` as keyof typeof stratum]}
            isSkeleton={isLoading}
            labelId={`${fractionType}FractionSorting`}
            label='Sorting'
          >
            <LookupField
              fieldId='lithologySorting'
              isSkeleton={isLoading}
              isDisabled={!allowableFields?.includes(FractionTypeAllowableFields.SO)}
              value={getLookupFieldValue(FractionTypeDescriptors.SORTING)}
              labelledBy={`${fractionType}FractionSorting`}
              setValue={({ value }) => handleFractionDescriptorValueChange(`${fractionType}Sorting`, value)}
            />
          </FormField>
          <FormField
            validationMessages={validationErrors[`${fractionType}FractionWeathering` as keyof typeof stratum]}
            isSkeleton={isLoading}
            labelId={`${fractionType}FractionWeathering`}
            label='Weathering'
          >
            <LookupField
              fieldId='lithologyWeathering'
              isSkeleton={isLoading}
              isDisabled={!allowableFields?.includes(FractionTypeAllowableFields.WE)}
              value={getLookupFieldValue(FractionTypeDescriptors.WEATHERING)}
              labelledBy={`${fractionType}FractionWeathering`}
              setValue={({ value }) => handleFractionDescriptorValueChange(`${fractionType}Weathering`, value)}
            />
          </FormField>
        </FormFieldRow>

        <FormFieldRow>
          <FormField
            validationMessages={validationErrors[`${fractionType}FractionRounding` as keyof typeof stratum]}
            isSkeleton={isLoading}
            labelId={`${fractionType}FractionRounding`}
            label='Rounding'
          >
            <LookupField
              fieldId='lithologyRounding'
              isSkeleton={isLoading}
              isDisabled={!allowableFields?.includes(FractionTypeAllowableFields.RO)}
              value={getLookupFieldValue(FractionTypeDescriptors.ROUNDING)}
              labelledBy={`${fractionType}FractionRounding`}
              setValue={({ value }) => handleFractionDescriptorValueChange(`${fractionType}Rounding`, value)}
            />
          </FormField>
          <FormField
            validationMessages={validationErrors[`${fractionType}FractionPlasticity` as keyof typeof stratum]}
            isSkeleton={isLoading}
            labelId={`${fractionType}FractionPlasticity`}
            label='Plasticity'
          >
            <LookupField
              fieldId='lithologyPlasticity'
              isSkeleton={isLoading}
              isDisabled={!allowableFields?.includes(FractionTypeAllowableFields.PL)}
              value={getLookupFieldValue(FractionTypeDescriptors.PLASTICITY)}
              labelledBy={`${fractionType}FractionPlasticity`}
              setValue={({ value }) => handleFractionDescriptorValueChange(`${fractionType}Plasticity`, value)}
            />
          </FormField>
        </FormFieldRow>

        {showFracturingField && (
          <FormFieldRow>
            <FormField
              validationMessages={validationErrors[`${fractionType}FractionFracturing` as keyof typeof stratum]}
              isSkeleton={isLoading}
              labelId={`${fractionType}FractionFracturing`}
              label='Fracturing'
            >
              <LookupField
                fieldId='lithologyFracturing'
                isSkeleton={isLoading}
                isDisabled={!allowableFields?.includes(FractionTypeAllowableFields.ST)}
                value={getLookupFieldValue(FractionTypeDescriptors.FRACTURING)}
                labelledBy={`${fractionType}FractionFracturing`}
                setValue={({ value }) =>
                  handleFractionDescriptorValueChange(`${fractionType}Fracturing` as keyof WellStratumFieldsEdit, value)
                }
              />
            </FormField>
          </FormFieldRow>
        )}
      </>
    </CollapsibleCard>
  )
}
